import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Youtube, Twitch, Plus, Trash2, Rss, MessageSquare, Zap, Radio, Save, Tv, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead, PremiumBanner, Toggle } from "@/components/kit";
import { ChannelSelect } from "@/components/ChannelSelect";
import { RoleSelect } from "@/components/RoleSelect";

const PLATFORMS = [
  { id: "youtube", label: "YouTube", icon: Youtube, color: "#ff0000" },
  { id: "twitch", label: "Twitch", icon: Twitch, color: "#9146ff" },
  { id: "kick", label: "Kick", icon: Zap, color: "#53fc18" },
  { id: "reddit", label: "Reddit", icon: MessageSquare, color: "#ff4500" },
  { id: "rss", label: "RSS Feed", icon: Rss, color: "#f26522" },
];

const HINTS = {
  youtube: { ph: "Channel ID (UC…) or @handle", help: "Paste the channel's ID (starts with UC) or its @handle." },
  twitch: { ph: "Twitch username, e.g. ninja", help: "The channel's login name. Needs Twitch API keys set on the server." },
  kick: { ph: "Kick username, e.g. xqc", help: "The Kick channel's username. Alerts when they go live." },
  reddit: { ph: "Subreddit, e.g. gaming", help: "A subreddit name (without r/). Alerts on new posts." },
  rss: { ph: "https://example.com/feed.xml", help: "Any RSS/Atom feed URL. Alerts on new items." },
};

const inputCls = "mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm";
const labelCls = "text-xs uppercase tracking-wider text-zinc-500";

export default function Social() {
  const { guildId, guild } = useGuild();
  const [tab, setTab] = useState("alerts");
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ platform: "youtube", creator: "", announce_channel: "announcements", message: "🔴 {creator} just went live! Come watch!", ping_role: "", button_label: "▶ Watch Stream" });
  const [golive, setGolive] = useState(null);
  const saveGolive = useUnsaved(golive, setGolive, () => saveGoliveRaw());
  const locked = false;

  const load = () => api.get(`/guilds/${guildId}/social`).then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get(`/guilds/${guildId}/discord/roles`).then((r) => setRoles(r.data.roles || [])).catch(() => {});
    api.get(`/guilds/${guildId}/modules/golive`).then((r) => setGolive(r.data)).catch(() => {});
  }, [guildId]);

  const add = async () => {
    if (!form.creator) return toast.error("Enter a creator/channel name");
    try { await api.post(`/guilds/${guildId}/social`, form); toast.success("Alert added"); setForm({ ...form, creator: "" }); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/guilds/${guildId}/social/${id}`); load(); };

  // ── Go Live config ──
  const gc = golive?.config || {};
  const setG = (k, v) => setGolive({ ...golive, config: { ...gc, [k]: v } });
  const allowed = gc.allowed_roles || [];
  const addAllowed = (v) => { const r = (v || "").trim().replace(/^@/, ""); if (!r || allowed.includes(r)) return; setG("allowed_roles", [...allowed, r]); };
  const removeAllowed = (r) => setG("allowed_roles", allowed.filter((x) => x !== r));
  const saveGoliveRaw = async () => {
    await api.put(`/guilds/${guildId}/modules/golive`, { enabled: golive.enabled, config: gc });
    toast.success("Go Live settings saved");
  };

  return (
    <div>
      <PageHead title="Social Alerts" desc="Announce when your favorite creators post or go live." />
      <PremiumBanner guildId={guildId} tier="Pro" locked={locked} />

      <div className="flex gap-1 border-b border-zinc-800 mb-6">
        <button data-testid="social-tab-alerts" onClick={() => setTab("alerts")} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 transition-colors ${tab === "alerts" ? "border-volt text-volt" : "border-transparent text-zinc-400 hover:text-white"}`}><Radio className="w-4 h-4" /> Creator Alerts</button>
        <button data-testid="social-tab-golive" onClick={() => setTab("golive")} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px flex items-center gap-2 transition-colors ${tab === "golive" ? "border-volt text-volt" : "border-transparent text-zinc-400 hover:text-white"}`}><Tv className="w-4 h-4" /> Go Live Command</button>
      </div>

      {tab === "alerts" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4 h-fit">
            <h3 className="font-head font-semibold">New alert</h3>
            <div><label className={labelCls}>Platform</label>
              <select data-testid="social-platform" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className={inputCls}>
                {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select></div>
            <div><label className={labelCls}>Creator / channel</label>
              <input data-testid="social-creator" value={form.creator} onChange={(e) => setForm({ ...form, creator: e.target.value })} placeholder={HINTS[form.platform]?.ph} className={inputCls} />
              <p className="text-[11px] text-zinc-600 mt-1">{HINTS[form.platform]?.help}</p></div>
            <div><label className={labelCls}>Announce channel</label>
              <ChannelSelect guildId={guildId} value={form.announce_channel} onChange={(v) => setForm({ ...form, announce_channel: v })} testid="social-announce-channel" placeholder="— select a channel —" /></div>
            <div><label className={labelCls}>Message · use {"{creator}"}</label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2} className={inputCls} /></div>
            <div><label className={labelCls}>Button label (link to the stream)</label>
              <input data-testid="social-button-label" value={form.button_label} onChange={(e) => setForm({ ...form, button_label: e.target.value })} placeholder="▶ Watch Stream" className={inputCls} />
              <p className="text-[11px] text-zinc-600 mt-1">Shown as a clickable link button under the alert. Leave blank to hide the button.</p></div>
            <div><label className={labelCls}>Ping role (optional)</label>
              <select data-testid="social-ping" value={form.ping_role} onChange={(e) => setForm({ ...form, ping_role: e.target.value })} className={inputCls}>
                <option value="">— no ping —</option>
                <option value="everyone">@everyone</option>
                <option value="here">@here</option>
                {roles.map((r) => <option key={r.id} value={r.id}>@{r.name}</option>)}
              </select></div>
            <button data-testid="social-add" onClick={add} className="w-full bg-volt text-volt-ink font-semibold rounded-md py-2 hover:bg-volt-hover flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add alert</button>
          </div>
          <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="font-head font-semibold mb-4">Active alerts ({items.length})</h3>
            <div className="space-y-2">
              {items.map((it) => {
                const P = PLATFORMS.find((p) => p.id === it.platform) || PLATFORMS[0];
                return (
                  <div key={it.id} data-testid="social-row" className="flex items-center gap-3 bg-black/30 border border-zinc-800 rounded-lg p-3">
                    <P.icon className="w-5 h-5 shrink-0" style={{ color: P.color }} />
                    <div className="flex-1 min-w-0"><div className="font-medium">{it.creator}</div><div className="text-xs text-zinc-500 truncate">→ #{it.announce_channel}</div></div>
                    <span data-testid="social-status" className="text-xs px-2 py-0.5 rounded-full bg-volt/15 text-volt">active</span>
                    <button data-testid="social-delete" onClick={() => del(it.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                );
              })}
              {items.length === 0 && <p className="text-zinc-600 text-sm">No alerts configured.</p>}
            </div>
          </div>
        </div>
      )}

      {tab === "golive" && (golive ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="golive-panel">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-head font-semibold flex items-center gap-2"><Tv className="w-4 h-4 text-[#9146ff]" /> /goinglive Command</h3>
                <p className="text-xs text-zinc-500 mt-1">Members run <code className="text-zinc-400">/goinglive</code>, paste their stream link in a private popup, and the bot announces it with a ping + Watch button.</p>
              </div>
              <Toggle testid="golive-enabled" checked={!!golive.enabled} onChange={(v) => setGolive({ ...golive, enabled: v })} />
            </div>

            <div><label className={labelCls}>Announcement channel</label>
              <ChannelSelect guildId={guildId} value={gc.channel} onChange={(v) => setG("channel", v)} testid="golive-channel" /></div>

            <div><label className={labelCls}>Ping role</label>
              <RoleSelect guildId={guildId} value={gc.ping_role} onChange={(v) => setG("ping_role", v)} testid="golive-ping-role" placeholder="— who to ping —" className="mt-1 w-full" /></div>

            <div>
              <label className={labelCls}>Allowed roles</label>
              <div className="text-[11px] text-zinc-600 mb-1">Only members with one of these can use the command. Leave empty to allow everyone.</div>
              <RoleSelect guildId={guildId} value="" onChange={addAllowed} testid="golive-allowed-role" placeholder="— add a role —" className="mt-1 w-full" />
              {allowed.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {allowed.map((r) => (
                    <span key={r} data-testid="golive-allowed-chip" className="inline-flex items-center gap-1 text-xs bg-volt/10 text-volt border border-volt/30 rounded-full pl-2.5 pr-1.5 py-1">
                      @{r}
                      <button onClick={() => removeAllowed(r)} className="hover:bg-volt/20 rounded-full p-0.5"><X className="w-3.5 h-3.5" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div><label className={labelCls}>Announcement message</label>
              <textarea data-testid="golive-message" value={gc.message ?? ""} onChange={(e) => setG("message", e.target.value)} rows={2} className={inputCls} />
              <div className="text-[11px] text-zinc-600 mt-1">Variables: {"{user}"}, {"{title}"}, {"{link}"}</div></div>

            <div className="grid grid-cols-2 gap-3 border-t border-zinc-800 pt-4">
              <div><label className={labelCls}>Live Now role</label>
                <div className="text-[11px] text-zinc-600 mb-1">Given to streamers while live.</div>
                <RoleSelect guildId={guildId} value={gc.live_role} onChange={(v) => setG("live_role", v)} testid="golive-live-role" placeholder="— none —" className="mt-1 w-full" /></div>
              <div><label className={labelCls}>Remove after (min)</label>
                <div className="text-[11px] text-zinc-600 mb-1">0 = keep until removed.</div>
                <input data-testid="golive-live-minutes" type="number" min="0" max="1440" value={gc.live_role_minutes ?? 120} onChange={(e) => setG("live_role_minutes", Math.max(0, Math.min(1440, +e.target.value)))} className={inputCls} /></div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><label className={labelCls}>Button label</label>
                <input data-testid="golive-button" value={gc.button_label ?? ""} onChange={(e) => setG("button_label", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Embed color</label>
                <input data-testid="golive-color" type="color" value={gc.embed_color || "#9146ff"} onChange={(e) => setG("embed_color", e.target.value)} className="mt-1 w-full h-10 bg-black border border-zinc-800 rounded-md cursor-pointer" /></div>
              <div><label className={labelCls}>Cooldown (min)</label>
                <input data-testid="golive-cooldown" type="number" min="0" max="1440" value={gc.cooldown_minutes ?? 5} onChange={(e) => setG("cooldown_minutes", Math.max(0, Math.min(1440, +e.target.value)))} className={inputCls} /></div>
            </div>

            <button data-testid="golive-save" onClick={saveGolive} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2.5 hover:bg-volt-hover flex items-center gap-2 text-sm"><Save className="w-4 h-4" /> Save Go Live settings</button>
          </div>

          {/* Live preview */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 h-fit">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-3">Discord preview</div>
            {gc.ping_role && <div className="text-[13px] text-volt mb-1">@{gc.ping_role}</div>}
            <div className="force-dark bg-[#313338] rounded-lg p-3 text-sm">
              <div className="rounded border-l-4 pl-3 py-2 force-dark bg-[#2b2d31]" style={{ borderColor: gc.embed_color || "#9146ff" }}>
                <div className="flex items-center gap-2 mb-1">
                  {guild?.icon ? <img src={guild.icon} alt="" className="w-5 h-5 rounded-full" /> : <div className="w-5 h-5 rounded-full bg-zinc-700" />}
                  <span className="text-xs font-semibold text-zinc-200">Streamer</span>
                </div>
                <div className="font-semibold text-white">Playing something awesome</div>
                <div className="text-zinc-300 text-[13px] mt-0.5 whitespace-pre-wrap">{(gc.message || "").replace("{user}", "@Streamer").replace("{title}", "Playing something awesome").replace("{link}", "")}</div>
                <div className="text-[12px] text-[#00a8fc] mt-1">Stream · Watch now</div>
              </div>
              <button className="mt-2 text-white text-[13px] font-medium px-3 py-1.5 rounded" style={{ background: "#4e5058" }}>{gc.button_label || "▶ Watch Stream"}</button>
            </div>
            <p className="text-[11px] text-zinc-600 mt-3">Members will get a private popup to paste their link — nobody else sees it until the bot posts here.</p>
          </div>
        </div>
      ) : <p className="text-zinc-500 text-sm">Loading…</p>)}
    </div>
  );
}
